import { Plus } from "@phosphor-icons/react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { type Columns, DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { useSession } from "@/features/auth/useSession";
import type { MasterType } from "@/features/master-types/api";
import { masterTypesQuery } from "@/features/master-types/queries";

/**
 * The registry: every master type this tenant defines, and the way in to
 * each one's schema.
 *
 * Still the DataTable's reference implementation — six states, keyset
 * pagination, localised display names — but no longer read-only. PR5
 * added the schema builder each row links to, and creating a type, which
 * is the step before a schema can be drafted against it at all.
 */
export const Route = createFileRoute("/_authed/master-types/")({
  staticData: { crumbs: [{ key: "nav.masterTypes" }] },
  component: MasterTypes,
});

function MasterTypes() {
  const { t } = useTranslation();
  const { me, can } = useSession();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const query = useInfiniteQuery(masterTypesQuery(me.resolved_locale));
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);

  // Client-side, and only over what has already been fetched — the list
  // endpoint has no `?q=`. Honest for a registry of a few dozen types;
  // records get server-side filtering in PR5, where the row count makes
  // the difference matter.
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === "") return rows;
    return rows.filter(
      (row) =>
        row.code.toLowerCase().includes(needle) ||
        Object.values(row.display_name).some((name) => name.toLowerCase().includes(needle)),
    );
  }, [rows, search]);

  const columns = useMemo<Columns<MasterType>>(
    () => [
      {
        id: "display_name",
        // `display_name` is a `{locale: value}` map, resolved here rather
        // than server-side: the registry list is small and a caller may
        // want to see that a translation is missing.
        accessorFn: (row: MasterType) =>
          row.display_name[me.resolved_locale] ??
          row.display_name[me.tenant.default_locale] ??
          Object.values(row.display_name)[0] ??
          "",
        header: t("masterTypes.columns.name"),
        // Name over code, per the design, rather than two columns. The
        // code still has to be *findable* — it is the identity the API
        // takes — so it is rendered rather than dropped, and the filter
        // above still matches on it. `accessorFn` keeps returning the
        // name alone so sorting and filtering do not see the code glued
        // onto it.
        // `truncate` here is inert *as this branch stands*, and measuring
        // it is the only way that was ever going to be discovered:
        // `DataTable`'s table is auto-layout inside an `overflow-x-auto`
        // container, so a long name widens the table and the container
        // scrolls — nothing makes the cell narrower than its content.
        // Measured on an 83-character display name at both 1280 and 375:
        // `scrollWidth === clientWidth` in every cell, while the wrapper
        // went 677/341. So this is not one of the contract's
        // truncated-text tooltip sites, however much it looks like one,
        // and `TruncatedText` would attach a tooltip that could never
        // open.
        //
        // It stops being inert the day something caps the column, which
        // is precisely what PR #62 does at widths below `lg` — it bounds
        // the pinned first column at half the viewport so the actions to
        // its right stay reachable. **Once both land, revisit this and
        // `users.index.tsx`**: below `lg` these cells will genuinely
        // clip, and the tooltip case will apply to them after all.
        // Left as-is rather than stripped for exactly that reason.
        // (DEE-52)
        cell: ({ row, getValue }) => (
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-foreground">{String(getValue())}</span>
            <span className="truncate font-mono text-muted text-xs">{row.original.code}</span>
          </span>
        ),
      },
      {
        id: "is_active",
        accessorKey: "is_active",
        header: t("masterTypes.columns.status"),
        cell: ({ getValue }) => <StatusBadge status={getValue() ? "active" : "inactive"} />,
      },
    ],
    [t, me.resolved_locale, me.tenant.default_locale],
  );

  return (
    <>
      <PageHeader
        title={t("nav.masterTypes")}
        description={t("masterTypes.description")}
        help={t("pageHelp.masterTypes")}
        actions={
          can("master_type.define") ? (
            <Button asChild tooltip={t("tooltips.createType")}>
              <Link to="/master-types/new">
                <Plus aria-hidden="true" />
                {t("schema.createType.action")}
              </Link>
            </Button>
          ) : undefined
        }
      />
      <DataTable
        caption={t("nav.masterTypes")}
        columns={columns}
        data={filtered}
        getRowId={(row) => row.id}
        isLoading={query.isPending}
        isFetching={query.isFetching && !query.isFetchingNextPage}
        error={query.error}
        onRetry={() => void query.refetch()}
        hasMore={query.hasNextPage}
        isLoadingMore={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
        search={{ value: search, onChange: setSearch }}
        onClearFilters={() => setSearch("")}
        // Opening a type means opening its schema — the fields it
        // declares are the only thing a master type actually *is*.
        onRowActivate={(row) =>
          void navigate({ to: "/master-types/$code/schema", params: { code: row.code } })
        }
        activeFilters={
          search === "" ? [] : [{ field: "search", label: `${t("table.search")}: ${search}` }]
        }
      />
    </>
  );
}
